import { useEffect, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import {
  FAILURE_EVENT_TAGS,
  isModuleLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveFailureError,
} from "../error-telemetry";
import { useClientActionMap } from "../actions/use-client-action-map";
import type { LoadedModule, ModuleLoader } from "../module-loader";
import { markModuleReadyForReadiness } from "../performance-telemetry";
import { useViewIdentity } from "../view-identity";

import type { ModuleID, VersionString } from "@bestowinc/tinkertoy-core";

import { getModuleLoadPromise } from "./module-read-resource";

// ============================================================================
// ModuleRenderer
// ============================================================================

export type ModuleRendererProps = {
  moduleId: ModuleID;
  /**
   * Exact module version the renderer mounts (drawn from the loader's
   * `resolvedModuleVersions` map / pinned by the view manifest). Used to
   * fetch the bundle and forwarded to the server on every action POST so
   * it can re-validate the request against the same manifest.
   */
  version: VersionString;
  /** Used to fetch the bundle when this module key is not yet cached. */
  loader: ModuleLoader;

  /** Already-resolved props (ModuleRef callback has been invoked). */
  props: unknown;
  /** Server-fetched data for this module. */
  data: unknown;

  /** For branch modules — pre-rendered slot content. */
  slots?: Record<string, ReactNode>;
  /** For layout modules — main content. */
  children?: ReactNode;

  /** Shown while the bundle is loading (uses the shared preload cache; no `Suspense` required). */
  loadingFallback?: ReactNode;
};

type LoadState =
  | { status: "pending" }
  | { status: "ready"; definition: LoadedModule["definition"] }
  | { status: "error"; error: unknown };

/**
 * Loads one module bundle from the CDN and renders `leaf` | `branch` | `layout`
 * with `actions` / `props` / `data` (+ `slots` | `children` as appropriate).
 *
 * Loads subscribe via {@link getModuleLoadPromise} (same cached promise as preload in `<ViewRenderer>`).
 *
 * View-level identity (viewId / viewVersion / apiBaseUrl) is read from
 * `<ViewIdentityProvider>`; mounting outside one throws when the loaded
 * module first calls `useClientActionMap`.
 *
 * `props` and `data` are passed by reference; the renderer does not mutate them.
 */
export function ModuleRenderer({
  moduleId,
  version,
  loader,
  props,
  data,
  slots,
  children,
  loadingFallback,
}: ModuleRendererProps): ReactNode {
  const [state, setState] = useState<LoadState>({ status: "pending" });
  const viewIdentity = useViewIdentity();

  useEffect(() => {
    let cancelled = false;
    setState({ status: "pending" });

    void getModuleLoadPromise(loader, moduleId, version).then(
      (loaded) => {
        if (cancelled) {
          return;
        }
        setState({ status: "ready", definition: loaded.definition });
        markModuleReadyForReadiness({
          viewId: viewIdentity.viewId,
          viewVersion: viewIdentity.viewVersion,
          moduleId,
          version,
        });
      },
      (err: unknown) => {
        if (cancelled) {
          return;
        }
        setState({ status: "error", error: err });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [moduleId, version, loader, viewIdentity.viewId, viewIdentity.viewVersion]);

  if (state.status === "pending") {
    return loadingFallback ?? null;
  }

  if (state.status === "error") {
    if (isModuleLoadError(state.error)) {
      const failureError = resolveFailureError(
        state.error,
        `Failed to load module "${moduleId}" (${version}).`,
      );
      reportFailureOnce(failureError, {
        tag: FAILURE_EVENT_TAGS.moduleBundleLoad,
        errorCode: state.error.code,
        message: failureError.message,
        error: failureError,
        moduleId: state.error.moduleId,
        moduleVersion: state.error.version,
      });
      markFailureReported(state.error);
    }
    throw state.error;
  }

  return (
    <ModuleRendererLoaded
      moduleVersion={version}
      definition={state.definition}
      props={props}
      data={data}
      slots={slots}
      children={children}
    />
  );
}

type ModuleRendererLoadedProps = Pick<
  ModuleRendererProps,
  "props" | "data" | "slots" | "children"
> & {
  moduleVersion: VersionString;
  definition: LoadedModule["definition"];
};

/**
 * Sub-component so `useClientActionMap` is only called once a definition exists.
 */
function ModuleRendererLoaded({
  moduleVersion,
  definition,
  props,
  data,
  slots,
  children,
}: ModuleRendererLoadedProps): ReactNode {
  const actions = useClientActionMap(definition, { moduleVersion });

  let element: ReactNode;
  switch (definition.type) {
    case "leaf": {
      const Leaf = definition.component;
      element = <Leaf actions={actions} props={props} data={data} />;
      break;
    }
    case "branch": {
      const Branch = definition.component;
      element = (
        <Branch actions={actions} props={props} data={data} slots={slots ?? {}} />
      );
      break;
    }
    case "layout": {
      const Layout = definition.component;
      element = (
        <Layout actions={actions} props={props} data={data}>
          {children}
        </Layout>
      );
      break;
    }
    case "error": {
      throw new Error(
        `Module "${definition.id}" has type "error" and cannot be mounted through ModuleRenderer. Error modules are rendered only by ShellErrorBoundary.`,
      );
    }
    default: {
      throw new Error("Unreachable: unexpected module definition shape");
    }
  }

  // The renderer no longer wraps modules in `<EnrollmentProvider>` /
  // `<CarrierProvider>`. A module that reads carrier/enrollment context bundles
  // its own provider+hook and self-wraps its tree from `data` (the `store` still
  // arrives in `data`). Wrapping here would put the provider in the shell bundle
  // and the hook in the module bundle — the cross-bundle context mismatch this
  // change fixes.
  return element;
}
